import { chartData } from '../../lead-forms/reporting-adapter'
import { useLeadFormStats } from '../../lead-forms/use-lead-form-stats'
import { useMemo } from 'react'
import {
	BOT_OUTCOMES,
	EMAIL_OUTCOMES,
	OutcomeDef,
	OutcomePoint,
	getBotOutcomeSeries,
	getEmailOutcomeSeries,
	outcomeTotals,
	previousOutcomeTotals,
	useFormSpam
} from '../form-spam-data'
import { TrafficQualityPanel, countRows } from './form-detail-drawer'

/**
 * R104. The traffic quality tray, on the Form Spam HOME page.
 *
 * The drawer already draws this pair for ONE form. The home page asks the same
 * question about the whole account, so this file reuses the drawer's
 * `TrafficQualityPanel` verbatim and only changes what it is fed. Nothing about
 * the charts is written twice.
 *
 * R171: the Over time / Breakdown switch is gone. Esteban dropped the
 * Breakdown draw, so the pair only draws the day-by-day stacks.
 *
 * R191: the tray container is gone too. The two charts are cards of their own
 * now, so this file holds the data and no chrome at all.
 *
 * R105: the range does NOT live here. It moved up to the page header, where a
 * date picker normally sits, because it now cuts the table as well as the
 * charts. A control that reaches the whole page must not sit inside one card.
 *
 * The tray is account-wide on purpose. The page header says "Summary metrics
 * for <site>", and the pills under it cut the FORMS table, not the charts. A
 * tray that followed the pills would make the same control mean two things.
 */

/**
 * How far back every number on the Form Spam home page looks.
 *
 * The page holds the value and the page header draws the control. The type
 * lives here because the tray is the only other file that reads it, and
 * `form-spam-data.tsx` belongs to a parallel lane right now.
 */
export type DashboardRange = '7d' | '30d'

/**
 * Every form's day-by-day series, added up day by day.
 *
 * All series come from the same `DAY_LABELS` array, so day `i` is the same date
 * in every one of them and the sum can go by index. The first series carries
 * the labels; an account with no form at all returns nothing and the charts
 * draw their own empty state.
 */
const sumSeries = (series: OutcomePoint[][]): OutcomePoint[] => {
	const first = series[0]
	if (!first) return []
	return first.map((point, index) => {
		const counts: Record<string, number> = {}
		series.forEach((one) => {
			const day = one[index]
			if (!day) return
			Object.keys(day.counts).forEach((key) => {
				counts[key] = (counts[key] ?? 0) + day.counts[key]
			})
		})
		return { day: point.day, counts }
	})
}

/** The axis ladder for one stack, from its tallest day. */
const bedOf = (points: OutcomePoint[], defs: OutcomeDef[]) =>
	countRows(
		Math.max(
			1,
			...points.map((point) =>
				defs.reduce((sum, def) => sum + (point.counts[def.key] ?? 0), 0)
			)
		)
	)

export const TrafficQualityTray = ({ range }: { range: DashboardRange }) => {
	const { forms, api } = useFormSpam()
	const report = useLeadFormStats(
		api,
		range === '7d' ? 7 : 30,
		undefined,
		forms
	)

	const emailSeries = useMemo(
		() =>
			api ? [] : sumSeries(forms.map((form) => getEmailOutcomeSeries(form))),
		[forms]
	)
	const botSeries = useMemo(
		() =>
			api ? [] : sumSeries(forms.map((form) => getBotOutcomeSeries(form))),
		[forms]
	)
	const emailVisible = useMemo(
		() => (range === '7d' ? emailSeries.slice(-7) : emailSeries),
		[range, emailSeries]
	)
	const botVisible = useMemo(
		() => (range === '7d' ? botSeries.slice(-7) : botSeries),
		[range, botSeries]
	)

	// R108: each legend row also carries its change against the period before.
	// The mock holds 30 days and nothing earlier, so the earlier window is
	// drawn from the same seeded generator, not sliced out of the series. The
	// seed says `account` because this tray adds every form together; the
	// drawer seeds on one form id, so the two surfaces answer differently for
	// the same outcome, which is what they should do.
	const email = useMemo(() => {
		const totals = outcomeTotals(emailVisible, EMAIL_OUTCOMES)
		return {
			points: emailVisible,
			defs: EMAIL_OUTCOMES as OutcomeDef[],
			bed: bedOf(emailVisible, EMAIL_OUTCOMES),
			totals,
			prev: previousOutcomeTotals(
				totals,
				EMAIL_OUTCOMES,
				`account/email/${range}`
			)
		}
	}, [emailVisible, range])
	const bot = useMemo(() => {
		const totals = outcomeTotals(botVisible, BOT_OUTCOMES)
		return {
			points: botVisible,
			defs: BOT_OUTCOMES,
			bed: bedOf(botVisible, BOT_OUTCOMES),
			totals,
			prev: previousOutcomeTotals(totals, BOT_OUTCOMES, `account/bot/${range}`)
		}
	}, [botVisible, range])

	// R191 (Esteban, 2026-09-01): the big gray "Traffic quality" tray is gone.
	// `TrafficQualityPanel` draws the two charts as cards of their own, so this
	// file only feeds it. The section title left with the tray: each card names
	// itself, the way the dashboard cards do.
	if (api && report.error) return <p role="alert">{report.error}</p>
	if (api) {
		const realEmail = chartData(report.stats, 'email')
		const realBot = chartData(report.stats, 'bot')
		return (
			<TrafficQualityPanel
				loading={report.loading}
				email={{
					...realEmail,
					defs: EMAIL_OUTCOMES,
					bed: bedOf(realEmail.points, EMAIL_OUTCOMES)
				}}
				bot={{
					...realBot,
					defs: BOT_OUTCOMES,
					bed: bedOf(realBot.points, BOT_OUTCOMES)
				}}
			/>
		)
	}
	return <TrafficQualityPanel email={email} bot={bot} />
}
